import { type MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  readPersistedMultiAgentSession,
  resetMultiAgentForTenantChange,
  useMultiAgentStore,
} from "@/stores/multiAgentStore";
import { useChatPendingInputStore } from "@/stores/chatPendingInputStore";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { fetchMultiAgentRunDetail, isMultiAgentAuthError } from "./useRunDetail";
import {
  hydrateMultiAgentAnalysis,
  type MultiAgentRunAnalysis,
} from "@/pages/multi-agent/ide/store/analysisHydration";

const FINAL_STATUSES = new Set(["done", "failed", "cancelled"]);

function clearRunSearchParam(setSearchParams: ReturnType<typeof useSearchParams>[1]): void {
  setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.delete("run");
    next.delete("session");
    return next;
  }, { replace: true });
}

export interface UseRunHydrationOptions {
  runs: Array<{ id: string; session_id?: string | null; started_at?: string | null }>;
}

export interface UseRunHydrationReturn {
  hydratedRunId: string | null;
  setHydratedRunId: (id: string | null) => void;
  handledQueryRunRef: MutableRefObject<string | null>;
  resolvedRunId: string | null;
  /** Call before consuming a pending intent to prevent auto-load-latest racing with it. */
  blockAutoLoad: () => void;
}

/**
 * Handles URL-driven and auto-load session hydration for the multi-agent route.
 *
 * Responsibilities:
 * - Resolves ?run= / ?session= query params to a target run ID
 * - Hydrates that run from the API into the store
 * - On first mount with no URL target, opens the most-recent session
 * - Detects user/org switches and clears stale cache (tenant isolation)
 * - Handles "Improve with AI" intent handoff (prefills composer, blocks auto-load)
 */
export function useRunHydration({ runs }: UseRunHydrationOptions): UseRunHydrationReturn {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hydratedRunId, setHydratedRunId] = useState<string | null>(null);

  const querySessionId = searchParams.get("session");
  const queryRunId = searchParams.get("run");

  const resolvedRunId = useMemo(() => {
    if (queryRunId) return queryRunId;
    if (!querySessionId) return null;
    const inSession = runs.filter((r) => r.session_id === querySessionId);
    if (inSession.length === 0) return null;
    return inSession
      .slice()
      .sort((a, b) => (b.started_at || "").localeCompare(a.started_at || ""))[0].id;
  }, [queryRunId, querySessionId, runs]);

  const latestRunId = useMemo(() => {
    if (!runs.length) return null;
    return runs
      .slice()
      .sort((a, b) => (b.started_at || "").localeCompare(a.started_at || ""))[0].id;
  }, [runs]);

  const handledQueryRunRef = useRef<string | null>(null);
  const didAutoLoadRef = useRef(false);

  const hydrateRun = async (targetRunId: string) => {
    if (useMultiAgentStore.getState().runId === targetRunId) return;
    const runIdBeforeFetch = useMultiAgentStore.getState().runId;
    const detail = await fetchMultiAgentRunDetail(queryClient, targetRunId);
    if (useMultiAgentStore.getState().runId !== runIdBeforeFetch) return;
    const currentSessionId = useMultiAgentStore.getState().sessionId;
    const preserveSession = Boolean(
      currentSessionId && detail.run.session_id && currentSessionId === detail.run.session_id,
    );
    const { reset, hydrateFromDetail, startRun } = useMultiAgentStore.getState();
    reset();
    hydrateFromDetail(detail, { preserveSession });
    hydrateMultiAgentAnalysis((detail as { analysis?: MultiAgentRunAnalysis }).analysis);
    setSearchParams({}, { replace: true });
    if (FINAL_STATUSES.has(detail.run.status)) {
      setHydratedRunId(targetRunId);
    } else {
      // Capture the server-side start time that hydrateFromDetail just
      // restored from `started_at`. startRun() always stamps Date.now() as
      // runStartedAt (correct for a fresh send), which would reset the
      // elapsed timer to ~0 on every page refresh. We restore the real
      // server timestamp after startRun so the timer continues from the
      // actual run start rather than the refresh moment.
      const serverStartedAt = useMultiAgentStore.getState().runStartedAt;
      setHydratedRunId(null);
      startRun(targetRunId, detail.run.user_intent);
      if (serverStartedAt != null) {
        useMultiAgentStore.setState({ runStartedAt: serverStartedAt });
      }
    }
  };

  const onHydrateFailure = (err: unknown) => {
    handledQueryRunRef.current = null;
    setHydratedRunId(null);
    if (isMultiAgentAuthError(err)) {
      resetMultiAgentForTenantChange();
      clearRunSearchParam(setSearchParams);
      toast.error("Could not open this run — it may belong to another organization.");
      return;
    }
    toast.error("Could not restore session. Please try again.");
  };

  // ── URL-driven hydration ───────────────────────────────────────────────────
  useEffect(() => {
    if (!resolvedRunId || handledQueryRunRef.current === resolvedRunId) return;
    handledQueryRunRef.current = resolvedRunId;
    (async () => {
      try { await hydrateRun(resolvedRunId); }
      catch (e) { onHydrateFailure(e); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedRunId]);

  // ── Auto-load most-recent session on first mount ───────────────────────────
  useEffect(() => {
    if (didAutoLoadRef.current) return;
    if (resolvedRunId || querySessionId) return;
    if (useMultiAgentStore.getState().runId) { didAutoLoadRef.current = true; return; }
    // A pending intent (Fix / Improve with AI handoff) means the caller is about
    // to start a fresh run. Block auto-load so we don't hydrate the previous
    // terminal run's content into the thread before the new run starts — that
    // would push the streaming answer below the fold, making the page look idle.
    if (useChatPendingInputStore.getState().multiAgentIntent) {
      didAutoLoadRef.current = true;
      return;
    }
    const persisted = readPersistedMultiAgentSession();
    const targetRunId = persisted.runId || latestRunId;
    if (!targetRunId) return;
    didAutoLoadRef.current = true;
    handledQueryRunRef.current = targetRunId;
    (async () => {
      try { await hydrateRun(targetRunId); }
      catch (e) { onHydrateFailure(e); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedRunId, querySessionId, latestRunId]);

  // ── Tenant isolation ──────────────────────────────────────────────────────
  //
  // The ORG half of this is handled centrally: multiAgentStore registers its
  // teardown with `@/lib/org-scope`, so an org switch resets it however and
  // wherever it happens — not only while this hook is mounted. What remains here
  // is what only this component can do (clear the ?run= param and its own refs),
  // plus coverage for a change of USER, which the org scope does not observe.
  const tenantOrgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const tenantUserId = useAuthStoreV1((s) => s.user?.id ?? null);
  const prevTenantRef = useRef<string | null>(null);
  useEffect(() => {
    const key = tenantUserId && tenantOrgId ? `${tenantUserId}:${tenantOrgId}` : null;
    if (key && prevTenantRef.current && prevTenantRef.current !== key) {
      resetMultiAgentForTenantChange();
      void queryClient.removeQueries({ queryKey: ["multi-agent"] });
      clearRunSearchParam(setSearchParams);
      setHydratedRunId(null);
      handledQueryRunRef.current = null;
    }
    if (key) prevTenantRef.current = key;
  }, [tenantUserId, tenantOrgId, queryClient, setSearchParams]);

  const blockAutoLoad = () => { didAutoLoadRef.current = true; };

  return { hydratedRunId, setHydratedRunId, handledQueryRunRef, resolvedRunId, blockAutoLoad };
}
